import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../src/utils";
import {
  ExportBankStatement,
  ExportStatement,
  GenerateBankStatementApi,
  GetBankStatementsApi,
  GetStatementsApi,
  GenerateStatementForPeriodApi,
} from "./statementApi";
import { RootState } from "../../redux/store";
import axios from "axios";

export interface StatementsState {
  createStatementForPeriodStatus: QueryStatus;
  createBankStatementStatus: QueryStatus;
  statementsListStatus: QueryStatus;
  statementsList: GetStatementsApi[];
  bankStatementsListStatus: QueryStatus;
  bankStatementsList: GetBankStatementsApi[];
  createBankStatementsMultipleStatus: QueryStatus;
  statementExportStatus: QueryStatus;
  statementExportLink: ExportStatement;
  bankStatementExportStatus: QueryStatus;
  bankStatementExportLink: ExportBankStatement;
  statementDownloadStatus: QueryStatus;
  bankStatementDownloadStatus: QueryStatus;
}

const initialState: StatementsState = {
  createStatementForPeriodStatus: "idle",
  createBankStatementStatus: "idle",
  statementsListStatus: "idle",
  statementsList: [],
  bankStatementsListStatus: "idle",
  bankStatementsList: [],
  createBankStatementsMultipleStatus: "idle",
  statementExportStatus: "idle",
  statementExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  bankStatementExportStatus: "idle",
  bankStatementExportLink: {
    presignedUrl: null,
    fileName: null,
  },
  statementDownloadStatus: "idle",
  bankStatementDownloadStatus: "idle",
};

export const createStatementForPeriodAsync = createAsyncThunk(
  "createStatementForPeriod/call",
  async (payload: GenerateStatementForPeriodApi) => {
    const axios = authAxios();
    await axios.post(`statement_for_period`, payload);
  },
);

export const createBankStatementAsync = createAsyncThunk(
  "createBankStatements/call",
  async (payload: GenerateBankStatementApi) => {
    const axios = authAxios();
    await axios.post(`bank_statement`, payload);
  },
);

export const createBankStatementsMultipleAsync = createAsyncThunk(
  "createBankStatementsMultiple/call",
  async () => {
    const axios = authAxios();
    await axios.post(`bank_statements_multiple`);
  },
);

export const statementsListAsync = createAsyncThunk(
  "statementsList/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<GetStatementsApi[]>(`statements`);
    return response.data.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  },
);

export const bankStatementsListAsync = createAsyncThunk(
  "bankStatementsList/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<GetBankStatementsApi[]>(`bank_statements`);
    return response.data.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  },
);

export const statementExportAsync = createAsyncThunk(
  "statementExport/call",
  async (payload: { bucketFileName: string; fileName: string }) => {
    const axios = authAxios();
    const response = await axios.get<{ presignedUrl: string }>(
      `/statement/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const bankStatementExportAsync = createAsyncThunk(
  "bankStatementExport/call",
  async (payload: { bucketFileName: string; fileName: string }) => {
    const axios = authAxios();
    const response = await axios.get<{ presignedUrl: string }>(
      `/bank_statement/${payload.bucketFileName}/export`,
    );
    return {
      presignedUrl: response.data.presignedUrl,
      fileName: payload.fileName,
    };
  },
);

export const statementDownloadAsync = createAsyncThunk(
  "statementDownload/call",
  async (statementExportLink: ExportStatement) => {
    if (statementExportLink.presignedUrl && statementExportLink.fileName) {
      const response = await axios({
        url: statementExportLink.presignedUrl,
        method: "GET",
        responseType: "blob",
      });

      const a = document.createElement("a");
      a.download = statementExportLink.fileName || "yourStatementFile.xlsx";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      a.href = url;
      a.click();
    }
  },
);

export const bankStatementDownloadAsync = createAsyncThunk(
  "bankStatementDownload/call",
  async (bankStatementExportLink: ExportBankStatement) => {
    if (
      bankStatementExportLink.presignedUrl &&
      bankStatementExportLink.fileName
    ) {
      const response = await axios({
        url: bankStatementExportLink.presignedUrl,
        method: "GET",
        responseType: "blob",
      });

      const a = document.createElement("a");
      a.download =
        bankStatementExportLink.fileName || "yourBankStatementFile.pdf";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      a.href = url;
      a.click();
    }
  },
);

export const statementsSlice = createSlice({
  name: "statements",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetCreateStatementForPeriodStatus: (state) => {
      state.createStatementForPeriodStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(createStatementForPeriodAsync.pending, (state) => {
        state.createStatementForPeriodStatus = "processing";
      })
      .addCase(createStatementForPeriodAsync.fulfilled, (state) => {
        state.createStatementForPeriodStatus = "success";
      })
      .addCase(createStatementForPeriodAsync.rejected, (state) => {
        state.createStatementForPeriodStatus = "failed";
      })
      .addCase(createBankStatementAsync.pending, (state) => {
        state.createBankStatementStatus = "processing";
      })
      .addCase(createBankStatementAsync.fulfilled, (state) => {
        state.createBankStatementStatus = "success";
      })
      .addCase(createBankStatementAsync.rejected, (state) => {
        state.createBankStatementStatus = "failed";
      })
      .addCase(createBankStatementsMultipleAsync.pending, (state) => {
        state.createBankStatementsMultipleStatus = "processing";
      })
      .addCase(createBankStatementsMultipleAsync.fulfilled, (state) => {
        state.createBankStatementsMultipleStatus = "success";
      })
      .addCase(createBankStatementsMultipleAsync.rejected, (state) => {
        state.createBankStatementsMultipleStatus = "failed";
      })
      .addCase(statementsListAsync.pending, (state) => {
        state.statementsListStatus = "processing";
      })
      .addCase(statementsListAsync.fulfilled, (state, action) => {
        state.statementsList = action.payload;
        state.statementsListStatus = "success";
        state.createBankStatementStatus = "idle";
      })
      .addCase(statementsListAsync.rejected, (state) => {
        state.statementsListStatus = "failed";
      })
      .addCase(bankStatementsListAsync.pending, (state) => {
        state.bankStatementsListStatus = "processing";
      })
      .addCase(bankStatementsListAsync.fulfilled, (state, action) => {
        state.bankStatementsList = action.payload;
        state.bankStatementsListStatus = "success";
      })
      .addCase(bankStatementsListAsync.rejected, (state) => {
        state.bankStatementsListStatus = "failed";
      })
      .addCase(statementExportAsync.pending, (state) => {
        state.statementExportStatus = "processing";
      })
      .addCase(statementExportAsync.fulfilled, (state, action) => {
        state.statementExportLink = action.payload;
        state.statementExportStatus = "success";
      })
      .addCase(statementExportAsync.rejected, (state) => {
        state.statementExportStatus = "failed";
      })
      .addCase(bankStatementExportAsync.pending, (state) => {
        state.bankStatementExportStatus = "processing";
      })
      .addCase(bankStatementExportAsync.fulfilled, (state, action) => {
        state.bankStatementExportLink = action.payload;
        state.bankStatementExportStatus = "success";
      })
      .addCase(bankStatementExportAsync.rejected, (state) => {
        state.bankStatementExportStatus = "failed";
      })
      .addCase(statementDownloadAsync.pending, (state) => {
        state.statementDownloadStatus = "processing";
      })
      .addCase(statementDownloadAsync.fulfilled, (state) => {
        state.statementExportLink.presignedUrl = null;
        state.statementExportLink.fileName = null;
        state.statementDownloadStatus = "success";
        state.statementExportStatus = "idle";
      })
      .addCase(statementDownloadAsync.rejected, (state) => {
        state.statementDownloadStatus = "failed";
      })
      .addCase(bankStatementDownloadAsync.pending, (state) => {
        state.bankStatementDownloadStatus = "processing";
      })
      .addCase(bankStatementDownloadAsync.fulfilled, (state) => {
        state.bankStatementExportLink.presignedUrl = null;
        state.bankStatementExportLink.fileName = null;
        state.bankStatementDownloadStatus = "success";
        state.bankStatementExportStatus = "idle";
      })
      .addCase(bankStatementDownloadAsync.rejected, (state) => {
        state.bankStatementDownloadStatus = "failed";
      });
  },
});

export const selectStatementsList = (state: RootState) =>
  state.statement.statementsList;
export const selectStatementsListStatus = (state: RootState) =>
  state.statement.statementsListStatus;
export const selectBankStatementsList = (state: RootState) =>
  state.statement.bankStatementsList;
export const selectBankStatementsListStatus = (state: RootState) =>
  state.statement.bankStatementsListStatus;
export const selectBankStatementCreationStatus = (state: RootState) =>
  state.statement.createBankStatementStatus;
export const selectStatementForPeriodCreationStatus = (state: RootState) =>
  state.statement.createStatementForPeriodStatus;
export const selectBankStatementsMultipleCreationStatus = (state: RootState) =>
  state.statement.createBankStatementsMultipleStatus;
export const selectStatementExportStatus = (state: RootState) =>
  state.statement.statementExportStatus;
export const selectStatementExportLink = (state: RootState) =>
  state.statement.statementExportLink;
export const selectBankStatementExportStatus = (state: RootState) =>
  state.statement.bankStatementExportStatus;
export const selectBankStatementExportLink = (state: RootState) =>
  state.statement.bankStatementExportLink;

export default statementsSlice.reducer;
